// useThemeStore.ts
import type { AppTheme } from '@/modules/authentication/types';
import { useCallback } from 'react';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { setCookie, getCookie, setFavicon } from '../utils';

const FAVICON_LIGHT =
  'https://cdn.prod.website-files.com/68b6de6d1d27f7458deaf49f/68d5255f6d141934950b3410_favicon-light.svg';
const FAVICON_DARK =
  'https://cdn.prod.website-files.com/68b6de6d1d27f7458deaf49f/68d5255f165946e69a73cab4_favicon-dark.svg';

type ThemeState = {
  mode?: 'Dark' | 'Light';
  /** User preference from settings / profile (includes follow-system). */
  appTheme?: AppTheme;
  hasHydrated: boolean;
  setHasHydrated: (val: boolean) => void;
  /** Sets preference and resolved light/dark (cookie + favicon). */
  applyThemePreference: (preference: AppTheme) => void;
  /** Updates resolved mode only — for OS theme changes when preference is System Default. */
  applyResolvedModeFromSystem: (resolved: 'Light' | 'Dark') => void;
};

function detectSystemTheme(): 'Light' | 'Dark' {
  if (typeof window !== 'undefined' && window.matchMedia) {
    return window.matchMedia('(prefers-color-scheme: dark)').matches
      ? 'Dark'
      : 'Light';
  }
  return 'Light';
}

function syncResolvedAppearance(resolved: 'Light' | 'Dark') {
  if (typeof window !== 'undefined') {
    setCookie('theme', resolved);
  }
  setFavicon(resolved === 'Dark' ? FAVICON_DARK : FAVICON_LIGHT);
}

export const useThemeStore = create<ThemeState>()(
  persist(
    (set) => ({
      mode: undefined,
      appTheme: undefined,
      hasHydrated: false,
      setHasHydrated: (val: boolean) => set({ hasHydrated: val }),
      applyResolvedModeFromSystem: (resolved) => {
        set({ mode: resolved });
        syncResolvedAppearance(resolved);
      },
      applyThemePreference: (preference) => {
        const resolved =
          preference === 'System Default' ? detectSystemTheme() : preference;
        set({ appTheme: preference, mode: resolved });
        syncResolvedAppearance(resolved);
      },
    }),
    {
      name: 'theme',
      onRehydrateStorage: () => (state) => {
        if (typeof window !== 'undefined' && state) {
          const cookieTheme = getCookie('theme') as 'Dark' | 'Light' | null;

          // Migrate legacy persist: only `mode` was stored
          if (state.appTheme == null && state.mode) {
            state.appTheme = state.mode;
          }

          if (state.appTheme === 'System Default') {
            const resolved = detectSystemTheme();
            state.mode = resolved;
            setCookie('theme', resolved);
          } else if (state.appTheme === 'Light' || state.appTheme === 'Dark') {
            state.mode = state.appTheme;
            setCookie('theme', state.appTheme);
          } else {
             if (cookieTheme && !state.mode) {
              state.mode = cookieTheme;
            } else if (cookieTheme && state.mode && cookieTheme !== state.mode) {
              state.mode = cookieTheme;
            } else if (state.mode && !cookieTheme) {
              setCookie('theme', state.mode);
            }
            if (state.mode && state.appTheme == null) {
              state.appTheme = state.mode;
            }
          }
        }
        state?.setHasHydrated(true);
      },
    }
  )
);

export const useUpdateThemeMode = () => {
  const applyThemePreference = useThemeStore((s) => s.applyThemePreference);

  return useCallback(
    (newValue: AppTheme | undefined) => {
      if (
        newValue !== 'Light' &&
        newValue !== 'Dark' &&
        newValue !== 'System Default'
      ) {
        return;
      }
      applyThemePreference(newValue);
    },
    [applyThemePreference]
  );
};
